/**
 * useAiTaskLauncher — the call-site helper for AI task launcher entries.
 *
 * Wraps `openAiChatWithTaskUngated` in the existing CLI gate so a user with
 * neither Claude Code nor Codex installed gets `AiChatCliRequiredDialog`
 * instead of a chat sidebar that can never answer. This is the **only**
 * launcher entry point exported from the feature — Object Explorer, Schema
 * Overview, Query Editor and the compare surfaces all go through it.
 *
 * Usage:
 * ```tsx
 * const { launchTask, cliAvailable, cliRequiredOpen, setCliRequiredOpen } = useAiTaskLauncher();
 * <MenuItem disabled={!cliAvailable} onClick={() => launchTask('explain-table', ctx, 'object-explorer')} />
 * <AiChatCliRequiredDialog open={cliRequiredOpen} onOpenChange={setCliRequiredOpen} />
 * ```
 */

import { useCallback, useState } from 'react';
import { useCliDetection } from './useClaudeCliDetection';
import { openAiChatWithTaskUngated } from '../launchers/openAiChatWithTask';
import type { AiTaskContext, AiTaskId, AiTaskSurface } from '../launchers/launcherRegistry';

export function useAiTaskLauncher() {
  const { canOpenChat, isLoading, checkAndOpenSidebar } = useCliDetection();
  const [cliRequiredOpen, setCliRequiredOpen] = useState(false);

  const launchTask = useCallback(
    (id: AiTaskId, ctx: AiTaskContext, surface: AiTaskSurface) => {
      checkAndOpenSidebar(
        () => {
          void openAiChatWithTaskUngated(id, ctx, surface);
        },
        () => setCliRequiredOpen(true),
      );
    },
    [checkAndOpenSidebar],
  );

  return {
    launchTask,
    /**
     * False → render the entry disabled with a WHY tooltip.
     *
     * Deliberately the *same* value `checkAndOpenSidebar` decides on, not a
     * second reading of "is a CLI installed": an entry must never advertise a
     * state the click then contradicts. In the web demo that means launchers
     * stay enabled with no CLI, matching the AI-chat toggle beside them.
     */
    cliAvailable: canOpenChat,
    cliDetectionLoading: isLoading,
    cliRequiredOpen,
    setCliRequiredOpen,
  };
}
